// @ts-strict-ignore
import type { SnippetVersion, SnippetDetails, SnippetVersionProps } from 'flow/service/snippets/types';
import type { AppDispatch, State } from '~/store';
import {
  appendPrimitiveToCollisionMap,
  buildPrimitives,
  getCollisionsWithinPrimitives,
  getSnippetActionsWithNonResolvableCollisions,
} from 'flow/service/snippets/collisions';
import { getEquivalencyKeyForAction, getEquivalencyKeyForPrimitive, getEquivalencyKeyForActionRules } from 'flow/service/snippets/collisions/equivalency';

import type {
  Action,
  DataRoute,
  PublicId,
  ReasonCodeGroup,
  Threshold,
  WorkflowInfo,
} from 'flow/store/engine/types';
import {
  selectActionByPublicId,
  selectFeatureFlagsForStore,
  selectOutcomes,
  selectPreSnippetWorkflowDataSnapshot,
  selectPublishedAttributes,
  selectReasonCodeGroups,
  selectReasonCodes,
  selectServices,
  selectSnippetActiveOperation,
  selectTags,
  selectWorkflowData,
  selectDefaultPrecedenceIndexedAction,
  selectNodeFolderState,
  selectActions,
  selectPreSnippetWorkflowPropsSnapshot,
  selectWorkflowIsSnippet,
  selectLists,
  selectCustomLists,
  selectLocalSnippetData,
  selectThresholds,
  selectDataRoutes,
  selectExternalWorkflowActions,
  selectGlobalServiceConfigOptions,
  selectUniversalAttributes,
} from '../selectors';
import {
  actionHasDataRoutes,
  actionHasRules,
  getPublishedDetails,
  includesActionOfType,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
} from 'flow/service/actions';
import {
  addThreshold,
  updateAction,
  addAction,
  addEphemeralTagToProps,
  addEphemeralOutcomeToProps,
  addEphemeralPublishedAttributeToProps,
  addEphemeralAttributeToProps,
  setSnippetMode,
  setSnippetActiveOperation,
  setPreSnippetWorkflowDataSnapshot,
  addEphemeralReasonCodeGroupToProps,
  applyWorkflowDataSnapshot,
  setSnippetPreviewData,
  setNodeFolderCollapsed,
  setPreSnippetWorkflowPropsSnapshot,
  applyWorkflowPropsSnapshot,
  updateDependencies,
  setSnippetIncompatibleActions,
  addEphemeralListToProps,
  addEphemeralCustomListToProps,
  setSnippetCollapsed,
  replaceWorkflowDataGraph,
  replaceWorkflowProps,
  clearWorkflowSnapshots,
  setFocusedNodeId,
} from '../slice';
import {
  sortSnippetInTopologicalOrder,
  resolveDataRouteSourceReferences,
  resolveThresholdParentReferences,
  pruneSnippetVersion,
  getIncompatibleActions,
  isActionSnippetCompatible,
  regenerateLinkedSnippetsPublicIds,
  getAggregationPublishedAttributeTokens,
} from 'flow/service/snippets/snippets';
import { applySnippetVersionUpgrade, buildLocalNextSnippet } from 'flow/service/snippets/operations/upgrade';
import {
  prepareLinkedSnippetForInsertion,
} from 'flow/service/snippets/operations/insert';
import {
  mergeActionRules,
  contextualizeSnippetForTargetWorkflow,
  isValidActionMerge,
} from 'flow/service/snippets/operations/merge';
import {
  getAllThresholdFromRules, indexThresholdsByPublicId,
} from 'flow/service/thresholds';
import { buildRuleState } from 'flow/service/rules';
import { getDataRoutesForActions } from 'flow/service/dataRoutes';
import { ActionType } from 'flow/store/engine/constants';
import { indexTagsByName } from 'flow/service/tags';
import { indexOutcomesByName } from 'flow/service/outcomes';
import { indexPublishedAttributesByName, isPublishedAttribute } from 'flow/service/publishedAttributes';
import { changeDescription, snippetMode, snippetOperation } from '../types';
import { buildDependencies } from '../../engine/dependencies/dependencies';
import { exitLinkedChildActionThunk, replaceDefaultPrecedenceIndexedAction } from './orchestration';
import {
  beginWorkflowHistoryTransaction,
  commitWorkflowHistoryTransaction,
  discardWorkflowHistoryTransaction,
} from './history';
import { Operation } from 'flow/service/changelog/types';
import { createSnapshot, createUiEffectContext } from 'flow/service/history';
import { batch } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { yieldToMain } from 'flow/service/util';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import type { PreparedSnippetInfo } from './types';
import { indexServicesByInternalName } from 'flow/service/services';
import {
  getAttributesForServices,
  getUsedAttributeTokens,
  indexServiceInternalNamesByAttributeToken,
} from 'flow/service/services/attributes';
import { isListAction } from 'flow/service/actions/services/lists';
import {
  indexCustomListsByName,
  isCustomListAction,
  getCustomListToken,
  EPHEMERAL_CUSTOM_LIST_VERSION,
} from 'flow/service/actions/services/customLists';
import { indexListsByName } from 'flow/service/lists';
import { generatePublicId } from 'flow/service/publicIds';
import { appendSnippetParent, isExternalSnippetChildAction } from 'flow/service/actions/snippets';
import type { SnippetScope } from 'flow/service/snippets';


function preparePreInsertSnapshotThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const { workflowEditor: state } = getState();

    const preInsertWorkflowDataSnapshot = createSnapshot({
      data: structuredClone(state.workflowData),
    });
    const preInsertWorkflowPropsSnapshot = createSnapshot({
      data: structuredClone(state.workflowProps),
    });

    dispatch(setPreSnippetWorkflowDataSnapshot({
      snapshot: preInsertWorkflowDataSnapshot,
    }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({
      snapshot: preInsertWorkflowPropsSnapshot,
    }));

    const isWorkflowHistoryOn = Boolean(
      selectFeatureFlagsForStore(getState())?.workflowEditorUndoRedo,
    );
    if (isWorkflowHistoryOn) {
      dispatch(beginWorkflowHistoryTransaction());
    }
  };
}

/**
 * Inserts a snippet version into the workflow with loading state and all other
 * UI requirements.
 * This is an orchestration wrapper that automatically sets the pastePending mode,
 * yields to the event loop for React to render the spinner, then runs the insertion, and should
 * be used when invoking this functionality from the UI.
 */
export function insertSnippetVersionThunk(args: {
  snippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch, getState: () => State) => {
    const { snippet } = args;
    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      // run core operation (blocking)
      dispatch(processSnippetVersionInsertionThunk({ snippet }));

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.insertSnippet,
        snippetActionPublicId: null,
      }));
      dispatch(setSnippetMode({ mode: snippetMode.review }));

      // open node folder if it has a child that was impacted by merge
      const { isCollapsed } = selectNodeFolderState(getState());
      const hasInputAttr = includesActionOfType({
        type: ActionType.INPUT,
        actions: Object.values(snippet.data.actions),
      });

      if (hasInputAttr && isCollapsed) {
        dispatch(setNodeFolderCollapsed({ isCollapsed: false }));
      }
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'InsertSnippetError';
      datadogRum.addError(error, {
        operation: 'insert_snippet',
        url: window.location.pathname,
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}


/**
 * Inserts a snippet version into the workflow.
 * This thunk contains the core logic for the insertion algorithm, and any pre and post
 * UI and state work leading to/from it should be handled in `insertSnippetVersionThunk` instead.
 *
 * # **This thunk should **NOT** be called from the UI directly.**
 */
export function processSnippetVersionInsertionThunk(args: {
  snippet: SnippetVersion;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const initialState = getState();

    // pre-process the Snippet
    const { snippet, incompatibleActions } = prepareSnippetForInsertion({
      state: initialState,
      snippet: args.snippet,
    });

    const snippetTagsByName = indexTagsByName(snippet.props.tags);
    const snippetOutcomesByName = indexOutcomesByName(snippet.props.outcomes);
    const snippetPublishedAttributesByName = indexPublishedAttributesByName(
      snippet.props.publishedAttributes,
    );
    const snippetPublishedAttributesByToken = new Map(
      snippet.props.publishedAttributes.map(pa => [pa.attributeToken, pa]),
    );
    const snippetServicesByInternalName = indexServicesByInternalName(snippet.props.services);
    const snippetListsByName = indexListsByName(snippet.props.lists);
    const snippetCustomListsByName = indexCustomListsByName(snippet.props.customLists || []);

    // We only need to add ephemeral attributes to props if they're actually in the snippet
    const usedAttributeTokens = getUsedAttributeTokens({
      actions: snippet.data.actions,
      thresholds: snippet.data.thresholds,
      dataRoutes: snippet.data.dataRoutes,
    });
    const snippetAttributes = getAttributesForServices(snippet.props.services, {
      includeOnly: usedAttributeTokens,
    });
    const snippetServiceInternalNameByAttrToken = indexServiceInternalNamesByAttributeToken(
      snippet.props.services,
    );

    const initialWorkflowData = selectWorkflowData(initialState);
    const initialAccountTags = selectTags(initialState);
    const initialAccountOutcomes = selectOutcomes(initialState);
    const initialAccountPublishedAttributes = selectPublishedAttributes(initialState);
    const initialAccountReasonCodeGroups = selectReasonCodeGroups(initialState);
    const initialAccountServices = selectServices(initialState);
    const initialAccountLists = selectLists(initialState);
    const initialAccountCustomLists = selectCustomLists(initialState);
    const initialAccountGlobalServiceConfigOptions = selectGlobalServiceConfigOptions(initialState);
    const initialAccountUniversalAttributes = selectUniversalAttributes(initialState);
    const workflowHasInitialDefaultAction = Boolean(
      selectDefaultPrecedenceIndexedAction(initialState),
    );

    /**
       * we build an initial collision map for the workflow primitives, and the insertion
       * algorithm will add new entries as it adds new ephemeral props.
       * action entries should not be added, as we need the initial action entries to avoid
       * merging duplicate snippet actions within each other.
       * if an up-to-date map with actions entries is needed, track those entries in a separate map
       */
    const workflowCollisionMap = getCollisionsWithinPrimitives({
      primitives: buildPrimitives({
        actions: initialWorkflowData.actions,
        services: initialAccountServices,
        tags: initialAccountTags,
        outcomes: initialAccountOutcomes,
        publishedAttributes: initialAccountPublishedAttributes,
        reasonCodeGroups: initialAccountReasonCodeGroups,
        lists: initialAccountLists,
        customLists: initialAccountCustomLists,
        globalServiceConfigOptions: initialAccountGlobalServiceConfigOptions,
        universalAttributes: initialAccountUniversalAttributes,
      }),
      context: {
        workflowData: {
          dataRoutes: initialWorkflowData.dataRoutes,
          services: initialAccountServices,
          globalServiceConfigOptions: initialAccountGlobalServiceConfigOptions,
        },
      },
    });

    // maps the id of a snippet action to the id of a merged wf action, if any
    const mergedActionMap = new Map<Action['publicId'], Action['publicId']>();

    // Batch all UI updates to re-render only once
    batch(() => {
      if (incompatibleActions.length) {
        dispatch(setSnippetIncompatibleActions({ actions: incompatibleActions }));
      }

      // Process ephemeral attributes from snippet services
      for (const snippetAttribute of snippetAttributes) {
        if (!snippetAttribute.isEphemeral) {
          /* eslint-disable-next-line no-continue */
          continue;
        }
        const serviceInternalName = snippetServiceInternalNameByAttrToken[
          snippetAttribute.attribute_token
        ];
        if (serviceInternalName == null) {
          throw new Error('No service found for ephemeral attribute');
        }

        const attributeEquivalencyKey = getEquivalencyKeyForPrimitive({
          primitive: {
            attribute: snippetAttribute,
            serviceInternalName,
          },
        });

        const hasAttributeCollision = workflowCollisionMap.has(attributeEquivalencyKey);

        if (!hasAttributeCollision) {
          appendPrimitiveToCollisionMap({
            collisionMap: workflowCollisionMap,
            equivalencyKey: attributeEquivalencyKey,
            primitive: {
              attribute: snippetAttribute,
              serviceInternalName,
            },
          });

          dispatch(addEphemeralAttributeToProps({
            attribute: snippetAttribute,
            serviceInternalName,
          }));
        }
      }

      // todo BUILD-3766 - support other all primitives besides Actions
      for (const snippetAction of Object.values(snippet.data.actions)) {
        const currentState = getState();
        const currentWorkflowData = selectWorkflowData(currentState);

        /**
         * Computes the equivalency key for the snippet action as it is evaluated.
         * This cannot be computed upfront, as we need the up-to-date merged action map.
         */
        const snippetActionEquivalencyKey = getEquivalencyKeyForAction({
          action: snippetAction,
          context: {
            actionMergeMap: mergedActionMap,
            workflowData: {
              dataRoutes: snippet.data.dataRoutes,
              services: snippetServicesByInternalName,
              globalServiceConfigOptions: snippet.props.globalServiceConfigOptions ?? [],
            },
          },
        });

        // ===== artifacts
        if (isActionOfType(snippetAction, ActionType.TAG)) {
          const snippetTagRelatedToSnippetAction = snippetTagsByName[snippetAction.tag.name];

          const tagEquivalenceKey = getEquivalencyKeyForPrimitive({
            primitive: snippetTagRelatedToSnippetAction,
          });
          const hasTagCollision = workflowCollisionMap.has(tagEquivalenceKey);

          if (!hasTagCollision) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: tagEquivalenceKey,
              primitive: snippetTagRelatedToSnippetAction,
            });

            dispatch(addEphemeralTagToProps({
              name: snippetTagRelatedToSnippetAction.tag_name,
              color: snippetTagRelatedToSnippetAction.tag_color,
              description: snippetTagRelatedToSnippetAction.description,
              isAdverseActionReason: snippetTagRelatedToSnippetAction.is_adverse_action_reason,
              isCaseCreating: snippetTagRelatedToSnippetAction.is_case_creating,
              suppressionInterval: snippetTagRelatedToSnippetAction.suppression_interval,
            }));
          }
        }

        if (isActionOfType(snippetAction, ActionType.OUTCOME)) {
          const snippetOutcomeRelatedToSnippetAction = (
            snippetOutcomesByName[snippetAction.outcome.name]
          );

          const outcomeEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetOutcomeRelatedToSnippetAction,
          });

          const hasOutcomeCollision = workflowCollisionMap.has(outcomeEquivalencyKey);

          if (!hasOutcomeCollision) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: outcomeEquivalencyKey,
              primitive: snippetOutcomeRelatedToSnippetAction,
            });

            dispatch(addEphemeralOutcomeToProps({
              name: snippetOutcomeRelatedToSnippetAction.outcome,
              color: snippetOutcomeRelatedToSnippetAction.outcome_color,
              isCaseStatus: snippetOutcomeRelatedToSnippetAction.is_case_status,
              isEventStatus: snippetOutcomeRelatedToSnippetAction.is_event_status,
            }));
          }
        }

        if (isPublishedAttribute(snippetAction)) {
          const snippetAttributeActionName = getPublishedDetails(snippetAction)?.attributeName;
          const snippetPublishedAttributeRelatedToAction = (
            snippetPublishedAttributesByName[snippetAttributeActionName]
          );

          const publishedAttributeEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetPublishedAttributeRelatedToAction,
          });

          const hasPublishedAttributeCollision = (
            workflowCollisionMap.has(publishedAttributeEquivalencyKey)
          );

          if (!hasPublishedAttributeCollision && snippetPublishedAttributeRelatedToAction) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: publishedAttributeEquivalencyKey,
              primitive: snippetPublishedAttributeRelatedToAction,
            });

            dispatch(addEphemeralPublishedAttributeToProps({
              attributeData: {
                ...snippetPublishedAttributeRelatedToAction,
              },
            }));
          }
        }

        if (isListAction(snippetAction)) {
          const snippetListRelatedToSnippetAction = snippetListsByName[snippetAction.service.name];

          const listEquivalencyKey = getEquivalencyKeyForPrimitive({
            primitive: snippetListRelatedToSnippetAction,
          });

          const hasListCollision = workflowCollisionMap.has(listEquivalencyKey);

          if (!hasListCollision && snippetListRelatedToSnippetAction) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: listEquivalencyKey,
              primitive: snippetListRelatedToSnippetAction,
            });

            dispatch(addEphemeralListToProps({ list: snippetListRelatedToSnippetAction }));
          }
        }

        if (isCustomListAction(snippetAction)) {
          const customListName = snippetAction.service.name;
          const snippetCustomListRelatedToAction = snippetCustomListsByName[customListName];

          if (snippetCustomListRelatedToAction) {
            const customListEquivalencyKey = getEquivalencyKeyForPrimitive({
              primitive: snippetCustomListRelatedToAction,
            });
            const hasCustomListCollision = workflowCollisionMap.has(customListEquivalencyKey);
            const customListToken = getCustomListToken(snippetAction);

            if (!hasCustomListCollision) {
              appendPrimitiveToCollisionMap({
                collisionMap: workflowCollisionMap,
                equivalencyKey: customListEquivalencyKey,
                primitive: snippetCustomListRelatedToAction,
              });

              dispatch(addEphemeralCustomListToProps({
                customList: {
                  ...snippetCustomListRelatedToAction,
                  name: customListName,
                  custom_list_token: customListToken,
                  active_major_version: EPHEMERAL_CUSTOM_LIST_VERSION,
                  active_columns: snippetCustomListRelatedToAction.active_columns || [],
                  description: snippetCustomListRelatedToAction.description,
                },
              }));
            }
          }
        }

        const hasEphemeralReasonCodeGroups = actionHasThresholdsWithEphemeralRCGs({
          action: snippetAction,
          thresholds: snippet.data.thresholds,
        });

        if (hasEphemeralReasonCodeGroups) {
          const ephemeralRCGForActionThresholds = getEphemeralReasonCodeGroupsFromActionThresholds({
            action: snippetAction,
            thresholds: snippet.data.thresholds,
          });

          for (const ephemeralRCG of ephemeralRCGForActionThresholds) {
            const ephemeralRCGEquivalencyKey = getEquivalencyKeyForPrimitive({
              primitive: ephemeralRCG,
            });

            const hasRCGCollision = workflowCollisionMap.has(ephemeralRCGEquivalencyKey);

            if (!hasRCGCollision) {
              appendPrimitiveToCollisionMap({
                collisionMap: workflowCollisionMap,
                equivalencyKey: ephemeralRCGEquivalencyKey,
                primitive: ephemeralRCG,
              });

              dispatch(addEphemeralReasonCodeGroupToProps({ reasonCodeGroup: ephemeralRCG }));
            }
          }
        }


        const collisionSetForSnippetAction = workflowCollisionMap.get(snippetActionEquivalencyKey);
        const hasActionCollision = (collisionSetForSnippetAction?.size ?? 0) > 0;

        if (!hasActionCollision) {
          /**
           * if evaluating a default indexed action (outcomes and workflow routing actions) and the
           * workflow already has one, the workflow's gets replaced with the snippet's.
           * this is the only different branch in logic for adding non-colliding actions, and should
           * run at most once, as there is at most one default action in a snippet.
           *
           * a snippet default action on a workflow that has none is added via the else branch below
           */
          if (workflowHasInitialDefaultAction
            && isDefaultPrecedenceIndexedAction(snippetAction)) {
            dispatch(replaceDefaultPrecedenceIndexedAction(snippetAction, {
              publicId: snippetAction.publicId,
            }));
          } else {
            let thresholdsToAdd: Threshold[] | undefined;
            if (actionHasRules(snippetAction)) {
              thresholdsToAdd = resolveThresholdsForAction({
                action: snippetAction,
                thresholds: snippet.data.thresholds,
                actionIdMap: mergedActionMap,
              });
            }

            let dataRoutesToAdd: DataRoute[] | undefined;
            if (actionHasDataRoutes(snippetAction, snippet.data.dataRoutes)) {
              dataRoutesToAdd = resolveDataRoutesForAction({
                action: snippetAction,
                dataRoutes: snippet.data.dataRoutes,
                actionIdMap: mergedActionMap,
              });
            }

            dispatch(addAction({
              action: snippetAction,
              thresholds: thresholdsToAdd,
              dataRoutes: dataRoutesToAdd,
            }));
          }
        } else {
          /**
           * grab the first-appearing colliding action.
           * further work can be done to manually pick which action to merge with.
           * - if picking a specific action, pass it into the thunk as a param in the payload.
          */
          const collidingActionPublicIds = [...collisionSetForSnippetAction];
          const firstAppearingCollidingActionPublicId = collidingActionPublicIds.at(0);

          const workflowActionToMergeInto = (
            selectActionByPublicId(firstAppearingCollidingActionPublicId)
          )(currentState);

          const mergeIsValid = isValidActionMerge({
            snippet: {
              actions: snippet.data.actions,
              thresholds: snippet.data.thresholds,
              dataRoutes: snippet.data.dataRoutes,
              actionToMerge: snippetAction,
            },
            workflow: {
              actions: currentWorkflowData.actions,
              thresholds: currentWorkflowData.thresholds,
              dataRoutes: currentWorkflowData.dataRoutes,
              actionToMergeInto: workflowActionToMergeInto,
            },
            actionMergeMap: mergedActionMap,
          });


          if (mergeIsValid) {
            mergedActionMap.set(snippetAction.publicId, firstAppearingCollidingActionPublicId);

            if (actionHasRules(snippetAction)) {
              const resolvedSnippetActionThresholds = resolveThresholdsForAction({
                action: snippetAction,
                thresholds: snippet.data.thresholds,
                actionIdMap: mergedActionMap,
              });

              const snActionRulesEquivalencyKey = getEquivalencyKeyForActionRules({
                rules: snippetAction.rules,
                thresholds: indexThresholdsByPublicId(resolvedSnippetActionThresholds),
              });

              const wfActionRulesToMergeIntoEqKey = getEquivalencyKeyForActionRules({
                rules: workflowActionToMergeInto.rules,
                thresholds: currentWorkflowData.thresholds,
              });

              /**
               * when merging an Action, we want to avoid adding rules if they are equivalent to the
               * merged action's. this mostly happens when adding a snippet that has a lot of
               * overlap with the target workflow.
               */
              const hasRulesCollision = (
                snActionRulesEquivalencyKey === wfActionRulesToMergeIntoEqKey
              );
              if (!hasRulesCollision) {
                for (const snippetThreshold of resolvedSnippetActionThresholds) {
                  dispatch(addThreshold({ threshold: snippetThreshold }));
                }
                const mergedRuleTree = mergeActionRules({
                  snippetAction,
                  workflowAction: workflowActionToMergeInto,
                });

                const ruleStateToInsert = buildRuleState(mergedRuleTree);
                dispatch(updateAction({
                  path: `${workflowActionToMergeInto.publicId}.rules`,
                  value: ruleStateToInsert,
                  // skip auto snapshots & dependency compilation to improve performance
                  skipSnapshotAndDependencies: true,
                }));
              }
            }

            /* No need to resolve Data Routes here, since they are not merged like Rules are. */
          } else {
            let thresholdsToAdd: Threshold[] | undefined;
            if (actionHasRules(snippetAction)) {
              thresholdsToAdd = resolveThresholdsForAction({
                action: snippetAction,
                actionIdMap: mergedActionMap,
                thresholds: snippet.data.thresholds,
              });
            }

            /**
           * Data Routes do not need to be resolved here because the collision detection
           * algorithm makes it impossible for Outputs to create graph cycles, and Matrices
           * can only create cycles via their Rules, a scenario that is handled by resolving
           * their Thresholds
           */
            dispatch(addAction({
              action: snippetAction,
              thresholds: thresholdsToAdd,
            }));
          }
        }
      }

      // Dispatch ephemeral PAs referenced only in aggregation components
      const aggregationPATokens = getAggregationPublishedAttributeTokens(
        snippet.data.thresholds, snippet.data.dataRoutes,
      );
      for (const token of aggregationPATokens) {
        const snippetPA = snippetPublishedAttributesByToken.get(token);
        if (snippetPA) {
          const paEquivalencyKey = getEquivalencyKeyForPrimitive({ primitive: snippetPA });
          if (!workflowCollisionMap.has(paEquivalencyKey)) {
            appendPrimitiveToCollisionMap({
              collisionMap: workflowCollisionMap,
              equivalencyKey: paEquivalencyKey,
              primitive: snippetPA,
            });

            dispatch(addEphemeralPublishedAttributeToProps({
              attributeData: { ...snippetPA },
            }));
          }
        }
      }

      // dependencies should be updated manually at the end since we opted out
      dispatch(updateDependencies());
    });
  };
}

/**
 * Prepares a Snippet for insertion into the target account.
 * This is a normalization step that runs a series of operations on a Snippet
 * to simplify the downstream insertion algorithm. Returns the prepared snippet, and a list
 * of incompatible actions that were pruned from the snippet.
 *
 * Operations:
 * - **Prune Actions**: prunes incompatible actions out of the pasted snippet, if applicable.
 * - **Contextualization**: updates the names of the snippet primitives (and the corresponding actions),
 *                          that have collisions with the target account, as well as flags the props and
 *                          actions that are ephemeral, eliminating the need to run checks and updates
 *                          during insertion.
 * - **Topological Sort**: sorts the actions within the snippet in a topologically-valid order,
 *                         ensuring that every parent action is evaluated before any of its children.
 */
function prepareSnippetForInsertion(args: {
  snippet: SnippetVersion;
  state: State;
}): PreparedSnippetInfo {
  const { snippet, state } = args;

  const accountTags = selectTags(state);
  const accountOutcomes = selectOutcomes(state);
  const accountPublishedAttributes = selectPublishedAttributes(state);
  const accountReasonCodeGroups = selectReasonCodeGroups(state);
  const accountServices = selectServices(state);
  const accountReasonCodes = selectReasonCodes(state);
  const accountLists = selectLists(state);
  const accountCustomLists = selectCustomLists(state);
  const accountGlobalServiceConfigOptions = selectGlobalServiceConfigOptions(state);
  const accountUniversalAttributes = selectUniversalAttributes(state);
  const workflowActions = selectActions(state);
  const isSnippetWorkflow = selectWorkflowIsSnippet(state);

  const incompatibleActions = isSnippetWorkflow
    ? getIncompatibleActions({ snippet, test: isActionSnippetCompatible })
    : [];

  const prunedSnippet = pruneSnippetVersion({
    snippet,
    actionsToPrune: incompatibleActions,
  });

  const contextualizedSnippet = contextualizeSnippetForTargetWorkflow({
    snippet: prunedSnippet,
    accountPrimitives: {
      outcomes: accountOutcomes,
      publishedAttributes: accountPublishedAttributes,
      reasonCodeGroups: accountReasonCodeGroups,
      reasonCodes: accountReasonCodes,
      services: accountServices,
      tags: accountTags,
      lists: accountLists,
      customLists: accountCustomLists,
      globalServiceConfigOptions: accountGlobalServiceConfigOptions,
      universalAttributes: accountUniversalAttributes,
    },
    workflowPrimitives: {
      actions: workflowActions,
    },
  });

  const sortedSnippet = sortSnippetInTopologicalOrder({ snippet: contextualizedSnippet });

  return {
    snippet: sortedSnippet,
    incompatibleActions,
  };
}


function resolveThresholdsForAction(args: {
  action: Action;
  thresholds: Record<string, Threshold>;
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const {
    action,
    actionIdMap,
    thresholds,
  } = args;

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  return resolveThresholdParentReferences({
    actionIdMap,
    thresholds: actionThresholds,
  });
}

function resolveDataRoutesForAction(args: {
  action: Action;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
  actionIdMap: Map<Action['publicId'], Action['publicId']>;
}) {
  const { action, dataRoutes, actionIdMap } = args;


  const actionDataRoutes = getDataRoutesForActions({
    action,
    dataRoutes,
  });

  return resolveDataRouteSourceReferences({
    actionIdMap,
    dataRoutes: actionDataRoutes,
  });
}

export function resetSnippetUiThunk() {
  return (dispatch: AppDispatch) => {
    dispatch(setSnippetMode({ mode: null }));
    dispatch(setSnippetActiveOperation({ operation: null, snippetActionPublicId: null }));
    dispatch(setPreSnippetWorkflowDataSnapshot({ snapshot: null }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({ snapshot: null }));
    dispatch(setSnippetIncompatibleActions({ actions: [] }));
    dispatch(setFocusedNodeId(null));
  };
}

function actionHasThresholdsWithEphemeralRCGs(args: {
  action: Action;
  thresholds: Record<Threshold['publicId'], Threshold>;
}): boolean {
  const { action, thresholds } = args;
  if (!actionHasRules(action)) {
    return false;
  }

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  return actionThresholds.some(threshold => Boolean(threshold.right?.reasonCodeGroup));
}


function getEphemeralReasonCodeGroupsFromActionThresholds(args: {
  action: Action;
  thresholds: Record<Threshold['publicId'], Threshold>;
}): ReasonCodeGroup[] {
  const { action, thresholds } = args;

  if (!actionHasRules(action)) {
    return [];
  }

  const actionThresholds = getAllThresholdFromRules({
    rules: action.rules,
    thresholds,
  });

  const uniqueReasonCodeGroups = new Map<ReasonCodeGroup['name'], ReasonCodeGroup>();

  actionThresholds.forEach(threshold => {
    const thresholdRCG = threshold.right?.reasonCodeGroup;
    if (thresholdRCG?.isEphemeral) {
      uniqueReasonCodeGroups.set(thresholdRCG.name, thresholdRCG);
    }
  });

  return Array.from(uniqueReasonCodeGroups.values());
}

/**
 * Displays the snippet preview and updates the workflow snapshots.
 */
export function viewSnippetPreviewThunk(args: {
  snippetDetails: SnippetDetails;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const { snippet_data: snippetData, snippet_metadata: snippetMetadata } = args.snippetDetails;
    const state = getState();

    const preSnippetWorkflowDataSnapshot = createSnapshot({
      data: structuredClone(state.workflowEditor.workflowData),
    });
    const preSnippetWorkflowPropsSnapshot = createSnapshot({
      data: structuredClone(state.workflowEditor.workflowProps),
    });

    // Generate dependencies
    const snippetDependencies = buildDependencies({
      actions: snippetData.actions,
      thresholds: snippetData.thresholds,
      dataRoutes: snippetData.dataRoutes,
    });

    // Update workflow data with snippet data
    dispatch(setSnippetPreviewData({
      actions: snippetData.actions,
      thresholds: snippetData.thresholds,
      dataRoutes: snippetData.dataRoutes,
      dependencies: snippetDependencies,
      workflowToken: snippetData.token,
      name: snippetMetadata.name,
      publicVersion: snippetMetadata.published_version,
    }));

    dispatch(setSnippetMode({ mode: snippetMode.preview }));
    dispatch(setPreSnippetWorkflowDataSnapshot({ snapshot: preSnippetWorkflowDataSnapshot }));
    dispatch(setPreSnippetWorkflowPropsSnapshot({ snapshot: preSnippetWorkflowPropsSnapshot }));
  };
}

/**
 * Hydrates the workflowEditor store with a snippet's data and props as if it were a standalone workflow,
 * then enters snippet preview mode. Intended for marketplace contexts where no workflow is already loaded.
 */
export function viewStandaloneSnippetPreviewThunk(args: {
  snippetDetails: SnippetDetails;
  snippetVersionProps: SnippetVersionProps;
}) {
  return (dispatch: AppDispatch) => {
    const { snippetDetails, snippetVersionProps } = args;

    dispatch(replaceWorkflowProps({
      tags: snippetVersionProps.tags,
      outcomes: snippetVersionProps.outcomes,
      publishedAttributes: snippetVersionProps.publishedAttributes,
      reasonCodeGroups: snippetVersionProps.reasonCodeGroups,
      services: indexServicesByInternalName(snippetVersionProps.services ?? []),
      workflow: { type: snippetDetails.snippet_metadata.workflow_type } as WorkflowInfo,
    }));
    dispatch(replaceWorkflowDataGraph({
      actions: snippetDetails.snippet_data.actions,
      thresholds: snippetDetails.snippet_data.thresholds,
      dataRoutes: snippetDetails.snippet_data.dataRoutes,
    }));
    dispatch(clearWorkflowSnapshots());
    dispatch(setSnippetMode({ mode: snippetMode.preview }));
  };
}

export function undoInsertSnippetVersionThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();

    const workflowDataSnapshot = selectPreSnippetWorkflowDataSnapshot(state);
    const workflowPropsSnapshot = selectPreSnippetWorkflowPropsSnapshot(state);

    dispatch(discardWorkflowHistoryTransaction());

    if (!workflowDataSnapshot || !workflowPropsSnapshot) {
      return;
    }

    dispatch(applyWorkflowDataSnapshot({ snapshot: workflowDataSnapshot }));
    dispatch(applyWorkflowPropsSnapshot({ snapshot: workflowPropsSnapshot }));

    // update ui state
    dispatch(resetSnippetUiThunk());
  };
}

export function commitSnippetInsertionHistoryThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { operation: snippetOp, snippetActionPublicId } = selectSnippetActiveOperation(state);

    const isUpgrade = snippetOp === snippetOperation.upgradeLinkedSnippet;
    const operation = isUpgrade ? Operation.Modify : Operation.Add;
    // only present for linked snippets, regular snippets have no container node
    const publicId = snippetActionPublicId ?? undefined;

    dispatch(commitWorkflowHistoryTransaction({
      context: {
        operation,
        uiEffectContext: createUiEffectContext(changeDescription.snippet, { publicId }),
      },
    }));
  };
}

/**
 * Checks for non-resolvable action collisions and either inserts the snippet or
 * returns the collisions
 */
export function checkCollisionsAndInsertSnippetThunk(args: {
  snippet: {
    version: SnippetVersion;
    scope: SnippetScope;
  };
  supportsLinkedSnippets: boolean;
}) {
  return async (
    dispatch: AppDispatch,
    getState: () => State,
  ): Promise<{
    success: boolean;
    collisions?: Action[];
  }> => {
    const {
      snippet: {
        version: snippetVersion,
        scope,
      },
      supportsLinkedSnippets,
    } = args;

    const workflowActions = selectActions(getState());
    const collisions = getSnippetActionsWithNonResolvableCollisions({
      snippetActions: snippetVersion.data.actions,
      workflowActions: workflowActions ?? {},
    });

    if (collisions.length > 0) {
      dispatch(setSnippetMode({ mode: null }));
      return { success: false, collisions };
    }

    if (scope === 'local' && supportsLinkedSnippets) {
      await dispatch(insertLinkedSnippetVersionThunk({ snippet: snippetVersion }));
    } else {
      await dispatch(insertSnippetVersionThunk({ snippet: snippetVersion }));
    }
    return { success: true };
  };
}

// ==== linked snippets
export function insertLinkedSnippetVersionThunk(args: {
  snippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch) => {
    const { snippet } = args;

    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      /**
       * the snippet action id needs to be generated ahead of time so that the state for
       * its container can be created with the correct id
       */
      const parentSnippetActionPublicId = generatePublicId('action', ActionType.SNIPPET);
      dispatch(setSnippetCollapsed({
        isCollapsed: false,
        snippetId: parentSnippetActionPublicId,
      }));

      const {
        localToRemotePublicIds,
        snippet: remappedSnippet,
      } = regenerateLinkedSnippetsPublicIds({ snippet });

      // run blocking operation
      dispatch(processLinkedSnippetVersionInsertionThunk({
        snippet: remappedSnippet,
        localToRemotePublicIds,
        parentSnippetActionPublicId,
      }));

      dispatch(updateDependencies());

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.insertLinkedSnippet,
        snippetActionPublicId: parentSnippetActionPublicId,
      }));
      dispatch(setSnippetMode({ mode: snippetMode.review }));
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'InsertLinkedSnippetError';
      datadogRum.addError(error, {
        operation: 'insert_linked_snippet',
        url: window.location.pathname,
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}

export function processLinkedSnippetVersionInsertionThunk(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const {
      snippet: unpreparedSnippet,
      localToRemotePublicIds,
      parentSnippetActionPublicId,
    } = args;


    const snippet = prepareLinkedSnippetForInsertion({
      snippet: unpreparedSnippet,
      localToRemotePublicIds,
      parentSnippetActionPublicId,
    });


    const externalWorkflowActions = selectExternalWorkflowActions(getState());
    const workflowExternalActionsCollisionMap = getCollisionsWithinPrimitives({
      primitives: Object.values(externalWorkflowActions),
    });


    const mergedActionMap = new Map<PublicId, PublicId>();

    batch(() => {
      for (const snippetAction of Object.values(snippet.data.actions)) {
        if (isExternalSnippetChildAction(snippetAction)) {
          const equivalencyKey = getEquivalencyKeyForAction({ action: snippetAction });
          const collidingActionPublicIds = workflowExternalActionsCollisionMap.get(equivalencyKey);

          if ((collidingActionPublicIds?.size ?? 0) > 0) {
            // there will be at most 1 collision on external actions, so this is safe
            const [equivalentWorkflowActionPublicId] = collidingActionPublicIds.values();

            // mark the action as merged
            mergedActionMap.set(snippetAction.publicId, equivalentWorkflowActionPublicId);

            const equivalentWorkflowAction = selectActionByPublicId(
              equivalentWorkflowActionPublicId,
            )(getState());

            const updatedAction = appendSnippetParent({
              action: equivalentWorkflowAction,
              parentSnippetActionPublicId,
              remoteActionPublicId: localToRemotePublicIds[snippetAction.publicId],
            });

            // replace the whole action (has the same public id so only contents change)
            dispatch(updateAction({
              path: equivalentWorkflowAction.publicId,
              value: updatedAction,
              skipSnapshotAndDependencies: true,
            }));

            /**
             * merge the action and move on - we use continue because we still want to run the rest of the logic
             * for external actions without a collision
             */
            /* eslint-disable-next-line no-continue */
            continue;
          }
        }


        let thresholdsToAdd: Threshold[] | undefined;
        if (actionHasRules(snippetAction)) {
          thresholdsToAdd = resolveThresholdsForAction({
            action: snippetAction,
            actionIdMap: mergedActionMap,
            thresholds: snippet.data.thresholds,
          });
        }

        let dataRoutesToAdd: DataRoute[] | undefined;
        if (actionHasDataRoutes(snippetAction, snippet.data.dataRoutes)) {
          dataRoutesToAdd = resolveDataRoutesForAction({
            action: snippetAction,
            actionIdMap: mergedActionMap,
            dataRoutes: snippet.data.dataRoutes,
          });
        }

        dispatch(addAction({
          action: snippetAction,
          thresholds: thresholdsToAdd,
          dataRoutes: dataRoutesToAdd,
        }));
      }
    });
  };
}


export function upgradeLinkedSnippetVersionThunk(args: {
  localSnippetActionPublicId: PublicId;
  currentRemoteSnippet: SnippetVersion;
  nextRemoteSnippet: SnippetVersion;
}) {
  return async (dispatch: AppDispatch) => {
    // todo: BUILD-4349
    //
    try {
      // state and UI updates before running the blocking operation
      dispatch(setSnippetMode({ mode: snippetMode.pastePending }));
      dispatch(exitLinkedChildActionThunk());

      // yield control to main thread before running the blocking operation to render the spinner
      await yieldToMain();

      dispatch(preparePreInsertSnapshotThunk());

      dispatch(processLinkedSnippetVersionUpgradeThunk(args));

      dispatch(updateDependencies());

      // handle post merge ui changes below
      dispatch(setSnippetActiveOperation({
        operation: snippetOperation.upgradeLinkedSnippet,
        snippetActionPublicId: args.localSnippetActionPublicId,
      }));
      dispatch(setFocusedNodeId(args.localSnippetActionPublicId));
      dispatch(setSnippetMode({ mode: snippetMode.review }));
    } catch (error) {
      dispatch(undoInsertSnippetVersionThunk());
      error.name = 'UpgradeLinkedSnippetError';
      datadogRum.addError(error, {
        operation: 'upgrade_linked_snippet',
        url: window.location.pathname,
        // todo: finalize these
        // snippetToken: currentRemoteSnippet.metadata.token,
        // currentVersion: currentRemoteSnippet.metadata.version,
        // nextVersion: nextRemoteSnippet.metadata.version
      });
      captureExceptionsToSentry(error);
      throw error;
    }
  };
}


export function processLinkedSnippetVersionUpgradeThunk(args: {
  localSnippetActionPublicId: PublicId;
  currentRemoteSnippet: SnippetVersion;
  nextRemoteSnippet: SnippetVersion;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const {
      localSnippetActionPublicId,
      currentRemoteSnippet,
      nextRemoteSnippet,
    } = args;

    const initialState = getState();

    // since we currently only support linked snippets from the local library, we only need the graph data itself (no props)
    const currentLocalSnippetData = selectLocalSnippetData({
      snippetActionPublicId: localSnippetActionPublicId,
    })(initialState);

    const nextLocalSnippet = buildLocalNextSnippet({
      localCurrentSnippetData: currentLocalSnippetData,
      remoteCurrentSnippet: currentRemoteSnippet,
      localSnippetActionPublicId,
      remoteNextSnippet: nextRemoteSnippet,
    });

    const nextWorkflowData = applySnippetVersionUpgrade({
      localSnippetActionPublicId,
      snippet: nextLocalSnippet,
      workflow: {
        actions: selectActions(initialState),
        thresholds: selectThresholds(initialState),
        dataRoutes: selectDataRoutes(initialState),
      },
    });

    dispatch(replaceWorkflowDataGraph(nextWorkflowData));
  };
}
